import React from 'react';
import { Trip } from '../../types/entities';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

interface ReceiptConfirmationModalProps {
  open: boolean;
  trip: Trip;
  onConfirm: () => void;
  onSkip: () => void;
  onClose: () => void;
}

export const ReceiptConfirmationModal: React.FC<ReceiptConfirmationModalProps> = ({
  open,
  trip,
  onConfirm,
  onSkip,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!open) return null;

  const warehouseName = trip.deliveryWarehouse?.name || `Warehouse #${trip.deliveryId}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">{t('warehouse.receiptConfirmation.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('warehouse.receiptConfirmation.message')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Trip Info */}
          <div className="rounded-md border p-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-500">Trip ID:</div>
              <div className="font-medium">#{trip.id}</div>
              <div className="text-gray-500">Date:</div>
              <div className="font-medium">{new Date(trip.date).toLocaleDateString()}</div>
              <div className="text-gray-500">Warehouse:</div>
              <div className="font-medium">{warehouseName}</div>
            </div>
          </div>

          {/* Items Table */}
          <div>
            <h4 className="mb-2 font-medium">Items:</h4>
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Item</th>
                    <th className="px-4 py-2 text-right">Quantity</th>
                    <th className="px-4 py-2 text-left">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {trip.items?.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-4 py-2">{item.itemName}</td>
                      <td className="px-4 py-2 text-right">{item.volumeWeight}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Messages */}
          <div className="space-y-2 text-sm">
            <div className="rounded-md bg-green-50 p-3 text-green-800">
              <strong>{t('warehouse.receiptConfirmation.confirm')}:</strong>{' '}
              {t('warehouse.receiptConfirmation.infoConfirm')}
            </div>
            <div className="rounded-md bg-yellow-50 p-3 text-yellow-800">
              <strong>{t('warehouse.receiptConfirmation.skip')}:</strong>{' '}
              {t('warehouse.receiptConfirmation.infoSkip')}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onSkip}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('warehouse.receiptConfirmation.skip')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('warehouse.receiptConfirmation.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
